import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { compact, sortBy } from 'lodash';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faChevronRight, faTimes } from '@fortawesome/pro-light-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';

import { MiniSlider } from 'components/controls/MiniSlider';
import {
	getItemAlteredByScenario,
	vulnerabilityIndicators
} from 'utils/rules';
import HorizontalVulnerabilityScale from 'components/Radar/HorizontalVulnerabilityScale';

import Table from 'components/Table';
import { Button, Popover, Tag, Tooltip } from 'antd';
import Weight from 'components/controls/Weight';

import dayjs from 'dayjs';
import { IndicatorModel } from 'model/IndicatorModel';

import './IndicatorsList.sass';
import '../Risks/RiskPopover.sass';

interface ExtendedIndicatorModel extends IndicatorModel {
	index: number;
}

interface IndicatorsListProps {
	// List of indicators to display
	indicators: Array<IndicatorModel>;

	// List of available scenarios
	scenarios: Array<{
		id: string;
		name: string;
	}>;

	// Currently selected scenario
	scenario: {
		id: string;
		name?: string;
	} | null;

	// Callback function for updating an indicator
	onUpdateIndicator: (id: string, values: {
		trend?: string;
		weight?: number;
		indicatorCategoryId?: string | null;
	}) => void;

	// Catégories d'indicateurs du projet (colonne + remap via popover).
	indicatorCategories?: any[];
	// Affiche la colonne Catégorie (préférence d'affichage). Défaut vrai.
	showCategory?: boolean;

	// Callback function for editing an indicator
	onEditIndicator: (indicator: IndicatorModel) => void;

	// Option to disable interactions
	visibleColumns?: {
		edit?: boolean;
		id?: boolean;
		evaluatedAt?: boolean;
		name?: boolean;
		category?: boolean;
		status?: boolean;
		weight?: boolean;
	};

	readOnly?: boolean;
	showDescription?: boolean;
	selection?: any;
	deselect?: () => void;
	select?: (indicator: IndicatorModel) => void;
}

export const IndicatorsList = (props: IndicatorsListProps) => {
	const { t } = useTranslation();

	const {
		indicators = [],
		visibleColumns = {
			edit: true,
			id: true,
			evaluatedAt: true,
			name: true,
			category: true,
			status: true,
			weight: true
		},
		readOnly = false,
		showDescription = true,
		indicatorCategories = [],
		showCategory = true
	} = props;

	// Popover de réassignation de catégorie (clic sur la cellule, comme les
	// popovers impact/probabilité des risques).
	const [openCategoryPopover, setOpenCategoryPopover] = React.useState<string | null>(null);

	const allScenarios = (props.scenarios || []).concat([{ id: '', name: t('models.scenario.baseline') }]);
	let alteredIndicators = sortBy(
		indicators.map((c, index) => ({
			...getItemAlteredByScenario(c, props.scenario),
			index: index,
			key: c.id || `indicator-${index}` // Add a unique key for each element
		})
		),
		'name'
	) as ExtendedIndicatorModel[];

	let vulnerabilities = (props.scenarios || []).map((sc) => {
		return {
			id: sc.id,
			name: sc.name,
			vulnerability: vulnerabilityIndicators(
				indicators.map((c) => getItemAlteredByScenario(c, sc))
			)
		};
	});

	vulnerabilities.push({
		id: '',
		name: t('models.scenario.baseline'),
		vulnerability: vulnerabilityIndicators(indicators)
	});

	const selection = props.selection?.__typename === "Indicator" ? indicators.find((r: any) => r.id === props.selection?.id) : null;


	const columns = compact([
		!readOnly && visibleColumns['edit'] && {
			title: '',
			dataIndex: 'edit',
			key: 'edit',
			width: 50,
			render: (text: string, record: ExtendedIndicatorModel) => (
				<Tooltip title='Edit'>
					<Button
						shape="circle"
						onClick={() => props.onEditIndicator(record)}
					>
						<FontAwesomeIcon icon={faEdit as IconDefinition} />
					</Button>
				</Tooltip>
			)
		},
		visibleColumns['id'] && {
			title: '#',
			dataIndex: 'id',
			key: 'id',
			defaultSortOrder: 'ascend',
			width: 60,
			sortable: (a: ExtendedIndicatorModel, b: ExtendedIndicatorModel) => {
				const scoreA = (a.reference || ("I" + (a.index + 1))).toLocaleLowerCase()
				const scoreB = (b.reference || ("I" + (b.index + 1))).toLocaleLowerCase()

				if (scoreA < scoreB) return -1;
				if (scoreA > scoreB) return 1;
				return 0
			},
			render: (text: string, record: ExtendedIndicatorModel) => {
				return record.reference || ("I" + ((record.index + 1 + "").padStart(2, '0')))
			}
		},
		visibleColumns['evaluatedAt'] && {
			title: t("models.indicator.fields.evaluated_at"),
			dataIndex: 'evaluatedAt',
			key: 'evaluatedAt',
			className: 'nowrap',
			width: 140,
			sortable: (a: ExtendedIndicatorModel, b: ExtendedIndicatorModel) => {
				if (!b.evaluatedAt) return -1
				if (!a.evaluatedAt) return 1
				if (a.evaluatedAt && b.evaluatedAt && a.evaluatedAt < b.evaluatedAt) return -1;
				if (a.evaluatedAt && b.evaluatedAt > b.evaluatedAt) return 1;
				return 0
			},
			render: (text: string, record: ExtendedIndicatorModel, index: number) => {
				return <Tooltip title={record.evaluatedAt ? dayjs(record.evaluatedAt).format('DD/MM/YYYY HH[h]mm') : ''}>
					<div>
						{record.evaluatedAt ? dayjs(record.evaluatedAt).format('DD/MM/YYYY') : ''}
					</div>
				</Tooltip>
			}
		},
		visibleColumns['name'] && {
			title: t('models.indicator.fields.name'),
			dataIndex: 'name',
			key: 'name',
			className: 'w-50',
			sortable: true,
			render: (text: string, record: ExtendedIndicatorModel) => {
				return (
					<><span className={[
						"name cursor-pointer",
						record.id === selection?.id ? "selected" : ""
					].join(" ")}
						onClick={() => record.id === selection?.id ? props.deselect && props.deselect() : props.select && props.select(record)}>
						{record.name}

					</span>
						{showDescription && record.description ? (
							<div onClick={() => props.select && props.select(record)} className="description">
								{record.description}
							</div>
						) : (
							''
						)}</>
				);
			}
		},
		visibleColumns['category'] && showCategory && (indicatorCategories || []).length > 0 && {
			title: t('models.indicator.fields.indicator_category'),
			dataIndex: 'indicatorCategoryId',
			key: 'indicatorCategoryId',
			width: 180,
			sortable: (a: any, b: any) => {
				const nameA = indicatorCategories.find((c: any) => c.id === a.indicatorCategoryId)?.name;
				const nameB = indicatorCategories.find((c: any) => c.id === b.indicatorCategoryId)?.name;
				if (!nameA && !nameB) return 0;
				if (!nameA) return -1;
				if (!nameB) return 1;
				return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
			},
			render: (text: string, record: ExtendedIndicatorModel) => {
				const category = indicatorCategories.find(
					(c: any) => c.id === (record as any).indicatorCategoryId
				);
				const popoverKey = `category-${record.id}`;
				if (readOnly) return category ? category.name : '';
				// Clic -> popover de reassignation (pattern popovers des risques).
				return (
					<Popover
						trigger="click"
						className="cursor-pointer"
						open={openCategoryPopover === popoverKey}
						destroyTooltipOnHide
						onOpenChange={(visible) => {
							setOpenCategoryPopover(visible ? popoverKey : null);
						}}
						content={
							<div className="RiskPopover" style={{ maxWidth: 420 }}>
								{sortBy(indicatorCategories, [(c: any) => c.createdAt || '', 'name']).map((c: any) => (
									<Tag
										key={c.id}
										className={c.id === (record as any).indicatorCategoryId ? 'selected' : ''}
										onClick={() => {
											props.onUpdateIndicator(record.id, { indicatorCategoryId: c.id });
											setOpenCategoryPopover(null);
										}}
									>
										{c.name}
									</Tag>
								))}
								<Tag
									className={!(record as any).indicatorCategoryId ? 'selected' : ''}
									onClick={() => {
										props.onUpdateIndicator(record.id, { indicatorCategoryId: null });
										setOpenCategoryPopover(null);
									}}
								>
									{t('models.indicator_category.messages.no_x')}
								</Tag>
							</div>
						}
					>
						<span className="cursor-pointer">
							{category ? category.name : <span style={{ opacity: 0.4 }}>{'\u2014'}</span>}
						</span>
					</Popover>
				);
			}
		},
		visibleColumns['status'] && {
			title: 'Status',
			dataIndex: 'trend',
			key: 'trend',
			width: 280,
			sortable: (a: ExtendedIndicatorModel, b: ExtendedIndicatorModel) => {
				const scoreA = parseInt(a.trend)
				const scoreB = parseInt(b.trend)

				if (scoreA < scoreB) return -1;
				if (scoreA > scoreB) return 1;
				return 0
			},
			render: (text: string, record: ExtendedIndicatorModel) => {
				return (
					<MiniSlider
						className="mr-2"
						key={record.id}
						value={record.trend}
						min={0}
						max={100}
						style={{ width: '100%', flexShrink: 0 }}
						onChange={(val: string) =>
							props.onUpdateIndicator(record.id, { trend: val })
						}
						readOnly={readOnly}
						additionalMarkers={(allScenarios).map((sc) => {
							return {
								id: sc.id,
								name: sc.name || t("models.scenario.baseline"),
								value: getItemAlteredByScenario(record, sc).trend
							};
						})}
					/>
				);
			}
		},
		visibleColumns['weight'] && {
			title: t('models.indicator.fields.weight'),
			dataIndex: 'weight',
			key: 'weight',
			sortable: true,
			width: 100,
			render: (text: string, record: ExtendedIndicatorModel) => {
				const currentWeight = record.weight || 1;
				return (
					<Weight
						style={{ marginRight: '0.5rem' }}
						disabled={readOnly}
						onClick={(values: any) => {
							if (readOnly) return;
							let newWeight = currentWeight + 1;
							if (newWeight > 3) newWeight = 0;
							props.onUpdateIndicator(record.id, {
								...record,
								weight: newWeight
							});
						}}
					>
						{currentWeight}
					</Weight>
				);
			}
		},
		{
			title: '',
			dataIndex: 'select',
			key: 'select',
			width: 50,
			render: (text: string, record: ExtendedIndicatorModel) => (
				selection?.id !== record.id ? (
					<Tooltip title={t('actions.view')} placement='left'>
						<Button
							shape="circle"
							className={selection ? "discreet" : ""}
							onClick={() => props.select && props.select(record)}
						>
							<FontAwesomeIcon icon={faChevronRight as IconDefinition} />
						</Button>
					</Tooltip>
				) : (
					<Tooltip title={t('actions.close')} placement='left'>
						<Button
							shape="circle"
							onClick={() => props.deselect && props.deselect()}
						>
							<FontAwesomeIcon icon={faTimes as IconDefinition} />
						</Button>
					</Tooltip>
				)
			)
		}
	]);

	return (
		<div className="IndicatorsList d-flex w-100 animation-fade-in">
			<HorizontalVulnerabilityScale
				vulnerabilities={vulnerabilities}
				scenarios={props.scenarios}
				scenario={props.scenario}
			/>

			<div className="mb-3 mt-1 w-100 pr-2 pl-2" style={{ maxWidth: '100%' }}>
				<div className="scroll-container" style={{ overflowY: 'auto', width: '100%' }}>
					<Table
						pagination={false}
						dataSource={alteredIndicators}
						defaultSortOrder={["ascend"]}
						columns={columns}
						size="small"
						rowKey="id"
						className="mr-1"
						onRow={(record: any) => ({
							'data-testid': `indicator-list-item-${record.id}`,
							onDoubleClick: () => props.onEditIndicator(record),
						})}
					/>
				</div>
			</div>
		</div>
	);
};
